import React, { useEffect, useState } from 'react';
import { Bell, Loader2, Music, Save, Send, ShieldCheck, Timer } from 'lucide-react';

import { callingBehaviorConfigAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';

// 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md Phase A):
// call-center operations — AI disclosure (6.1), call limits + overflow (2.3),
// watchdog (2.4) and alerts (3.4). Stored in the account's calling settings
// document (AccountCallingBehaviorConfig.settings; defaults + validation in
// backend app/config/calling_defaults.py). Saves ONLY these keys — the
// backend merges partial settings, so other cards' settings are untouched.

type Ops = {
  recording_notice_enabled: boolean;
  recording_notice_text: string;
  intro_jingle_enabled: boolean;
  disclosure_enabled: boolean;
  disclosure_text: string;
  max_concurrent_calls: number;
  overflow_message: string;
  overflow_whatsapp_text: string;
  call_idle_timeout_seconds: number;
  call_max_duration_minutes: number;
  alert_emails: string[];
  alert_whatsapp_numbers: string[];
  alert_window_minutes: number;
  alert_cooldown_minutes: number;
  alert_provider_failures: number;
  alert_failed_calls: number;
  alert_slow_reply_ms: number;
  alert_slow_replies: number;
};

const KEYS: (keyof Ops)[] = [
  'recording_notice_enabled', 'recording_notice_text', 'intro_jingle_enabled',
  'disclosure_enabled', 'disclosure_text', 'max_concurrent_calls', 'overflow_message', 'overflow_whatsapp_text',
  'call_idle_timeout_seconds', 'call_max_duration_minutes', 'alert_emails', 'alert_whatsapp_numbers',
  'alert_window_minutes', 'alert_cooldown_minutes', 'alert_provider_failures', 'alert_failed_calls',
  'alert_slow_reply_ms', 'alert_slow_replies',
];

function fromApi(data: any): Ops {
  const e = data?.effective_settings || {};
  const out: any = {};
  KEYS.forEach((k) => {
    out[k] = e[k];
  });
  out.alert_emails = Array.isArray(e.alert_emails) ? e.alert_emails : [];
  out.alert_whatsapp_numbers = Array.isArray(e.alert_whatsapp_numbers) ? e.alert_whatsapp_numbers : [];
  return out as Ops;
}

function errorText(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
  return err?.message || 'Failed to save';
}

const splitList = (text: string) =>
  text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);

function NumberField({
  label, value, onChange, suffix, min, max,
}: { label: string; value: number; onChange: (n: number) => void; suffix?: string; min: number; max: number }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          className="h-8 w-24"
          min={min}
          max={max}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export default function CallingOperationsSettings() {
  const [ops, setOps] = useState<Ops | null>(null);
  const [emailsText, setEmailsText] = useState('');
  const [numbersText, setNumbersText] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const apply = (data: any) => {
    const next = fromApi(data);
    setOps(next);
    setEmailsText(next.alert_emails.join('\n'));
    setNumbersText(next.alert_whatsapp_numbers.join('\n'));
  };

  useEffect(() => {
    callingBehaviorConfigAPI.getConfig().then(apply).catch(() => setOps(null));
  }, []);

  if (!ops) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const set = (patch: Partial<Ops>) => setOps((o) => (o ? { ...o, ...patch } : o));

  const handleSave = async () => {
    setSaving(true);
    try {
      const settings: any = {};
      KEYS.forEach((k) => {
        settings[k] = ops[k];
      });
      settings.alert_emails = splitList(emailsText);
      settings.alert_whatsapp_numbers = splitList(numbersText);
      const updated = await callingBehaviorConfigAPI.updateConfig({ settings });
      apply(updated);
      toast({ title: 'Saved', description: 'Call-center settings updated (applies to new calls).' });
    } catch (err: any) {
      toast({ title: 'Save failed', description: errorText(err), variant: 'destructive' as any });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const data = await callingBehaviorConfigAPI.sendTestAlert();
      const r = data?.results || {};
      const lines = [...(r.email || []), ...(r.whatsapp || [])].map(
        (x: any) => `${x.to}: ${x.success ? 'sent' : `failed${x.error ? ` (${x.error})` : ''}`}`
      );
      toast({
        title: 'Test alert sent',
        description: lines.length ? lines.join(' · ') : 'Shown on the banner only — no email/WhatsApp recipients saved yet.',
      });
    } catch (err: any) {
      toast({ title: 'Test alert failed', description: errorText(err), variant: 'destructive' as any });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">Call-center operations</CardTitle>
        <CardDescription>
          What callers are told, how many calls you take at once, and who hears about problems.
          Changes apply to new calls.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 2026-10-04 call intro: recording notice + jazz sting, before the greeting */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium">
                <Music className="h-4 w-4" /> Recording notice
              </p>
              <p className="text-xs text-muted-foreground">
                Spoken first on every call in the Digital Companion's voice, translated into the caller's
                language. Callers can't talk over it.
              </p>
            </div>
            <Switch
              checked={ops.recording_notice_enabled}
              onCheckedChange={(on) => set({ recording_notice_enabled: on })}
            />
          </div>
          {ops.recording_notice_enabled && (
            <Input
              value={ops.recording_notice_text || ''}
              onChange={(e) => set({ recording_notice_text: e.target.value })}
              placeholder="This call will be recorded for training purposes."
            />
          )}
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm">Short jazz sound after the notice</p>
            <Switch
              checked={ops.intro_jingle_enabled}
              onCheckedChange={(on) => set({ intro_jingle_enabled: on })}
            />
          </div>
        </div>

        {/* 6.1 disclosure */}
        <div className="space-y-2 border-t border-border/60 pt-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="h-4 w-4" /> Tell callers they're talking to an AI
              </p>
              <p className="text-xs text-muted-foreground">
                Spoken first on every call, translated into the caller's language.
              </p>
            </div>
            <Switch checked={ops.disclosure_enabled} onCheckedChange={(on) => set({ disclosure_enabled: on })} />
          </div>
          {ops.disclosure_enabled && (
            <Input
              value={ops.disclosure_text || ''}
              onChange={(e) => set({ disclosure_text: e.target.value })}
              placeholder="You're speaking with an AI assistant."
            />
          )}
        </div>

        {/* 2.3 / 2.4 limits */}
        <div className="space-y-3 border-t border-border/60 pt-4">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Timer className="h-4 w-4" /> Call limits
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <NumberField
              label="Max calls at once (0 = no limit)"
              value={ops.max_concurrent_calls}
              onChange={(n) => set({ max_concurrent_calls: n })}
              min={0}
              max={1000}
            />
            <NumberField
              label="End a silent call after"
              value={ops.call_idle_timeout_seconds}
              onChange={(n) => set({ call_idle_timeout_seconds: n })}
              suffix="seconds"
              min={30}
              max={3600}
            />
            <NumberField
              label="Longest call"
              value={ops.call_max_duration_minutes}
              onChange={(n) => set({ call_max_duration_minutes: n })}
              suffix="minutes"
              min={5}
              max={480}
            />
          </div>
          {ops.max_concurrent_calls > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">When all lines are busy, the caller hears</Label>
                <Textarea rows={2} value={ops.overflow_message || ''} onChange={(e) => set({ overflow_message: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">…and gets this WhatsApp message</Label>
                <Textarea rows={2} value={ops.overflow_whatsapp_text || ''} onChange={(e) => set({ overflow_whatsapp_text: e.target.value })} />
              </div>
            </div>
          )}
        </div>

        {/* 3.4 alerts */}
        <div className="space-y-3 border-t border-border/60 pt-4">
          <div className="flex items-center justify-between gap-4">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Bell className="h-4 w-4" /> Alerts
            </p>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={handleTest} disabled={testing}>
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send test alert
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Always shown as a banner on the AI Calling page. Also sent to the people below. Save before testing.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Email addresses (one per line)</Label>
              <Textarea rows={2} value={emailsText} onChange={(e) => setEmailsText(e.target.value)} placeholder="ops@example.org" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">WhatsApp numbers (one per line)</Label>
              <Textarea rows={2} value={numbersText} onChange={(e) => setNumbersText(e.target.value)} placeholder="+254700000000" />
              <p className="text-[11px] text-muted-foreground">
                WhatsApp only delivers to a number that messaged your business number in the last 24 hours —
                email is the reliable channel.
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <NumberField label="Voice/speech provider failures" value={ops.alert_provider_failures} onChange={(n) => set({ alert_provider_failures: n })} suffix="times" min={1} max={1000} />
            <NumberField label="Failed calls" value={ops.alert_failed_calls} onChange={(n) => set({ alert_failed_calls: n })} suffix="calls" min={1} max={1000} />
            <NumberField label="Slow replies" value={ops.alert_slow_replies} onChange={(n) => set({ alert_slow_replies: n })} suffix="times" min={1} max={1000} />
            <NumberField
              label="A reply is slow after"
              value={Math.round(ops.alert_slow_reply_ms / 100) / 10}
              onChange={(n) => set({ alert_slow_reply_ms: Math.round(n * 1000) })}
              suffix="seconds"
              min={1}
              max={60}
            />
            <NumberField label="Within" value={ops.alert_window_minutes} onChange={(n) => set({ alert_window_minutes: n })} suffix="minutes" min={1} max={1440} />
            <NumberField label="Don't repeat an alert for" value={ops.alert_cooldown_minutes} onChange={(n) => set({ alert_cooldown_minutes: n })} suffix="minutes" min={1} max={1440} />
          </div>
        </div>

        <div className="flex justify-end border-t border-border/60 pt-4">
          <Button onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
